import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  loadStoredAuthError,
  resolveAuthErrorMessage,
  hasOAuthCallbackParams
} from '../../utils/authErrors';
import { AuthContext } from '../../contexts/AuthContext';
import { Loading, Logo } from '../../components/utility/Utility';
import GoogleSignInButton from '../../components/GoogleSignInButton';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    handleSignIn,
    handleSignInWithGoogle,
    authed,
    authReady,
    userProfile,
    handleCompleteNewPassword,
    handleForgotPassword,
    handleConfirmForgotPassword,
    authError,
    clearAuthError,
    restoreAuthErrorFromStorage,
    reportAuthError
  } = useContext(AuthContext);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const urlAuthError = useMemo(() => {
    const hasOAuthError =
      searchParams.get('error') || searchParams.get('error_description');
    if (!hasOAuthError) return '';
    return resolveAuthErrorMessage({ searchParams }) || '';
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('error') || searchParams.get('error_description')) {
      reportAuthError(null, searchParams);
    } else if (!authError) {
      restoreAuthErrorFromStorage();
    }
  }, [searchParams, authError, reportAuthError, restoreAuthErrorFromStorage]);

  const displayError = authError || urlAuthError || loadStoredAuthError() || formError;
  const isOAuthReturn = hasOAuthCallbackParams(location.search);
  const isRedirecting = authReady && authed && Boolean(userProfile);
  const isWaiting =
    submitting ||
    !authReady ||
    (authed && !userProfile) ||
    isRedirecting;
  const showStatusMessage =
    isWaiting &&
    !displayError &&
    (submitting || isOAuthReturn || authed);

  const renderLoginActions = () => (
    <div className="login-form__actions">
      {isWaiting ? (
        <Loading className="login-form__actions-loading" />
      ) : (
        <>
          <a className="login-form__text" href="#" onClick={onForgotPassword}>
            Forgot your password?
          </a>
          <button type="submit" className="gradient">
            Sign In
          </button>
          <GoogleSignInButton variant="signin" onClick={onGoogleSignIn} />
          <a className="login-form__text" href="/sign-up">
            Create Account
          </a>
        </>
      )}
    </div>
  );

  useEffect(() => {
    // This effect runs once when the component mounts to capture the redirect intent.
    if (searchParams.get('shopify_auth') === 'success') {
      sessionStorage.setItem('postAuthRedirect', '/app/integrations/shopify?polling=true');
    }
  }, [searchParams]);

  useEffect(() => {
    const inviteToken = searchParams.get('invite_token');
    if (inviteToken) {
      sessionStorage.setItem('invite_token', inviteToken);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!authReady || !authed || !userProfile) return;

    const inviteToken = sessionStorage.getItem('invite_token');
    if (inviteToken) {
      sessionStorage.removeItem('invite_token');
      navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
      return;
    }

    // Keep the query string and hash: deep links like the product analysis filters live there.
    const from = location.state?.from;
    const fromLocation = from?.pathname
      ? `${from.pathname}${from.search || ''}${from.hash || ''}`
      : null;
    const redirectPath = sessionStorage.getItem('postAuthRedirect');

    if (fromLocation && fromLocation.startsWith('/app')) {
      navigate(fromLocation, { replace: true });
    } else if (redirectPath) {
      sessionStorage.removeItem('postAuthRedirect');
      navigate(redirectPath);
    } else {
      navigate('/app/product-analysis/analytics');
    }
  }, [authReady, authed, userProfile, navigate, location.state, searchParams]);

  const onGoogleSignIn = async () => {
    setSubmitting(true);
    setFormError('');
    clearAuthError();
    try {
      await handleSignInWithGoogle();
    } catch (err) {
      setFormError(err.message || 'Google sign-in failed.');
      setSubmitting(false);
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    clearAuthError();
    try {
      const { nextStep } = await handleSignIn(email, password);
      if (nextStep && nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsNewPasswordRequired(true);
        setSubmitting(false);
      }
      // On success, keep submitting true until redirect unmounts this page.
    } catch (err) {
      setFormError(err.message || 'An unknown error occurred');
      setSubmitting(false);
    }
  };
  
  const onNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
        await handleCompleteNewPassword(newPassword);
        // On successful new password completion, the `authed` state will change,
        // triggering the useEffect to handle the correct redirect.
    } catch (err) {
        setFormError(err.message || 'An unknown error occurred');
        setSubmitting(false);
    }
  }

  const onForgotPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      await handleForgotPassword(email);
      setIsForgotPassword(true);
    } catch (err) {
      setFormError(err.message || 'An unknown error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmForgotPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      await handleConfirmForgotPassword(email, confirmationCode, newPassword);
      // After successful password reset, send them to login again
      setIsForgotPassword(false);
    } catch (err) {
      setFormError(err.message || 'An unknown error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (isForgotPassword) {
    return (
        <div className="login">
            <div className="form-container">
                <Logo className="login__logo" />
                <hr className="login__hr" />
                <h3>Reset Password</h3>
                <p>A code has been sent to {email}. Please enter it below to set a new password.</p>
                <form onSubmit={onConfirmForgotPassword} className="auth-form">
                    <label className="login-form__label" htmlFor="confirmationCode">Confirmation Code</label>
                    <input
                      id="confirmationCode"
                      type="text"
                      value={confirmationCode}
                      onChange={(e) => setConfirmationCode(e.target.value)}
                      disabled={submitting}
                      required
                    />

                    <label className="login-form__label" htmlFor="newPassword">New Password</label>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={submitting}
                      required
                    />

                    {formError && <div className="form-errors">{formError}</div>}
                    <div className="login-form__actions">
                      {submitting ? (
                        <Loading className="login-form__actions-loading" />
                      ) : (
                        <button type="submit" className="gradient">Set New Password</button>
                      )}
                    </div>
                </form>
            </div>
        </div>
    );
  }

  if (isNewPasswordRequired) {
    return (
        <div className="login">
            <div className="form-container">
                <Logo className="login__logo" />
                <hr className="login__hr" />
                <h3>Set a New Password</h3>
                <p>This is your first time logging in. Please set a new password.</p>
                <form onSubmit={onNewPasswordSubmit} className="auth-form">
                    <label className="login-form__label" htmlFor="newPassword">New Password</label>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={submitting}
                      required
                    />
                    {formError && <div className="form-errors">{formError}</div>}
                    <div className="login-form__actions">
                      {submitting ? (
                        <Loading className="login-form__actions-loading" />
                      ) : (
                        <button type="submit" className="gradient">Set Password and Sign In</button>
                      )}
                    </div>
                </form>
            </div>
        </div>
    );
  }

  return (
    <div className="login">
      <div className="form-container">
        <Logo className="login__logo" />
        <hr className="login__hr" />
        <form id="login-form" className="auth-form" onSubmit={onLogin}>
            <label className="login-form__label" htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="customer@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isWaiting}
              required
            />

            <label className="login-form__label" htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isWaiting}
              required
            />

            {showStatusMessage && (
              <p className="login-form__status">
                {isRedirecting
                  ? 'Redirecting…'
                  : isOAuthReturn
                    ? 'Completing sign-in…'
                    : 'Signing in…'}
              </p>
            )}

            {displayError && (
              <div className="form-errors" role="alert">
                {displayError}
              </div>
            )}
            {renderLoginActions()}
        </form>
      </div>
    </div>
  )
}
